import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Table, Badge, Nav, Tab } from 'react-bootstrap';
import Api from '../../services/Api';
// Importações para os gráficos
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Registrando os componentes do Chart.js
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement);

const Financial = ({ setLoading }) => {
  const [dolar, setDolar] = useState(null);

  // Estados para armazenar os dados processados
  const [dadosMilho, setDadosMilho] = useState(null);
  const [dadosSoja, setDadosSoja] = useState(null);
  const [dadosTrigo, setDadosTrigo] = useState(null);

  // Função para extrair dados do HTML das cotações
  const processarHtmlCotacoes = (html) => {
    if (!html) return null;

    // Cria um elemento DOM temporário para manipular o HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Extrai informações básicas
    const titulo = tempDiv.querySelector('.na-titulo')?.textContent.trim().split('\n')[0] || '';
    const fonte = tempDiv.querySelector('.na-fonte')?.textContent || '';
    const dataFechamento = tempDiv.querySelector('tfoot tr td div')?.textContent.trim() || '';

    // Extrai dados das cotações
    const cotacoes = [];
    const linhas = tempDiv.querySelectorAll('tbody tr');

    linhas.forEach(linha => {
      const celulas = linha.querySelectorAll('td');
      if (celulas.length >= 3) {
        cotacoes.push({
          praca: celulas[0].textContent.trim(),
          preco: parseFloat(celulas[1].textContent.trim().replace(',', '.')),
          variacao: parseFloat(celulas[2].textContent.trim().replace(',', '.').replace('+', ''))
        });
      }
    });

    return {
      titulo,
      fonte,
      dataFechamento,
      cotacoes
    };
  };


  const handleFinancial = async () => {
    const results = await Promise.allSettled([
      Api.get('/financial/130'),
      Api.get('/financial/127'),
      Api.get('/financial/225'),
      Api.get('https://economia.awesomeapi.com.br/last/USD-BRL')
    ]);
    

    const [milho, soja, trigo, dolar] = results;

    if (milho.status === "fulfilled")
      setDadosMilho(processarHtmlCotacoes(milho.value.data));

    if (soja.status === "fulfilled")
      setDadosSoja(processarHtmlCotacoes(soja.value.data));

    if (trigo.status === "fulfilled")
      setDadosTrigo(processarHtmlCotacoes(trigo.value.data));

    if (dolar.status === "fulfilled")
      setDolar(dolar.value.data.USDBRL);

    setLoading(false);
  };

  useEffect(() => {
    // Carrega ao entrar na tela
    handleFinancial();

    // Atualiza automaticamente a cada X minutos
    const INTERVAL = 5 * 60 * 1000; // 5 minutos

    const timer = setInterval(() => {
      handleFinancial();
    }, INTERVAL);

    // Cleanup (evita múltiplos intervalos)
    return () => clearInterval(timer);
  }, []);

  // Componente para renderizar gráficos baseados nos dados de cotações
  const GraficosCotacoes = ({ dados }) => {
    if (!dados || !dados.cotacoes || dados.cotacoes.length === 0) {
      return <Card.Body className="text-center">Sem dados disponíveis para gráficos</Card.Body>;
    }

    // Extrair dados para os gráficos
    const labels = dados.cotacoes.map(item => item.praca.split('/')[0]); // Pega apenas o nome da cidade
    const precos = dados.cotacoes.map(item => item.preco);
    const variacoes = dados.cotacoes.map(item => item.variacao);

    // Valores para referência
    const precoMedio = precos.reduce((a, b) => a + b, 0) / precos.length;
    const precoMax = Math.max(...precos);
    const precoMin = Math.min(...precos);

    // Função para criar um gradiente para área do gráfico
    const getGradient = (ctx, chartArea, cor1, cor2) => {
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      gradient.addColorStop(0, cor1);
      gradient.addColorStop(1, cor2);
      return gradient;
    };

    // Configuração do gráfico de barras para preços estilo financeiro
    const dadosBarras = {
      labels,
      datasets: [
        {
          label: 'Preço (R$/sc)',
          data: precos,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return 'rgba(16, 152, 84, 0.7)';
            return getGradient(ctx, chartArea, 'rgba(16, 152, 84, 0.1)', 'rgba(16, 152, 84, 0.7)');
          },
          borderColor: 'rgba(16, 152, 84, 1)',
          borderWidth: 1,
          barPercentage: 0.8,
          categoryPercentage: 0.7,
        },
      ],
    };

    // Configuração do gráfico de pizza para distribuição de preços
    const dadosPizza = {
      labels,
      datasets: [
        {
          data: precos,
          backgroundColor: [
            'rgba(0, 128, 0, 0.7)',     // Verde - positivo
            'rgba(0, 150, 136, 0.7)',   // Verde-água
            'rgba(33, 150, 243, 0.7)',  // Azul
            'rgba(156, 39, 176, 0.7)',  // Roxo
            'rgba(255, 193, 7, 0.7)',   // Amarelo - alerta
            'rgba(255, 152, 0, 0.7)',   // Laranja
            'rgba(244, 67, 54, 0.7)',   // Vermelho - negativo
          ],
          borderColor: [
            'rgba(0, 128, 0, 1)',
            'rgba(0, 150, 136, 1)',
            'rgba(33, 150, 243, 1)',
            'rgba(156, 39, 176, 1)',
            'rgba(255, 193, 7, 1)',
            'rgba(255, 152, 0, 1)',
            'rgba(244, 67, 54, 1)',
          ],
          borderWidth: 1,
        },
      ],
    };

    // Configuração do gráfico de linha para variações estilo mercado financeiro
    const dadosLinha = {
      labels,
      datasets: [
        {
          type: 'line',
          label: 'Média de Preço',
          data: Array(labels.length).fill(precoMedio),
          borderColor: 'rgba(255, 193, 7, 0.8)',
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
          fill: false,
        },
        {
          type: 'line',
          label: 'Preço (R$/sc)',
          data: precos,
          fill: {
            target: 'origin',
            above: (context) => {
              const chart = context.chart;
              const { ctx, chartArea } = chart;
              if (!chartArea) return 'rgba(0, 128, 0, 0.1)';
              return getGradient(ctx, chartArea, 'rgba(0, 128, 0, 0)', 'rgba(0, 128, 0, 0.2)');
            },
          },
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return 'rgba(0, 128, 0, 1)';
            return getGradient(ctx, chartArea, 'rgba(0, 128, 0, 0.1)', 'rgba(0, 128, 0, 0.8)');
          },
          borderColor: 'rgba(0, 128, 0, 1)',
          tension: 0.2,
          pointBackgroundColor: (context) => {
            const value = context.dataset.data[context.dataIndex];
            return value >= precoMedio ? 'rgba(0, 128, 0, 1)' : 'rgba(244, 67, 54, 1)';
          },
          pointBorderColor: '#fff',
          pointRadius: 5,
          pointHoverRadius: 7,
          pointBorderWidth: 2,
        },
        {
          type: 'bar',
          label: 'Variação (%)',
          data: variacoes,
          yAxisID: 'y1',
          backgroundColor: variacoes.map(val =>
            val >= 0 ? 'rgba(0, 128, 0, 0.7)' : 'rgba(244, 67, 54, 0.7)'
          ),
          borderColor: variacoes.map(val =>
            val >= 0 ? 'rgba(0, 128, 0, 1)' : 'rgba(244, 67, 54, 1)'
          ),
          borderWidth: 1,
          barPercentage: 0.5,
          categoryPercentage: 0.5,
        }
      ],
    };

    // Configuração do gráfico de área para preços - mais estilo financeiro
    const dadosArea = {
      labels,
      datasets: [
        {
          label: 'Preço (R$/sc)',
          data: precos,
          fill: true,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) return 'rgba(16, 152, 84, 0.2)';
            return getGradient(ctx, chartArea, 'rgba(16, 152, 84, 0)', 'rgba(16, 152, 84, 0.3)');
          },
          borderColor: 'rgba(16, 152, 84, 1)',
          borderWidth: 2,
          tension: 0.3,
          pointBackgroundColor: precos.map((val, idx) => {
            const prevVal = idx > 0 ? precos[idx - 1] : val;
            return val >= prevVal ? 'rgba(0, 128, 0, 1)' : 'rgba(244, 67, 54, 1)';
          }),
          pointBorderColor: '#fff',
          pointRadius: 4,
          pointHoverRadius: 6,
        }
      ],
    };

    // Opções do gráfico
    const opcoesBarras = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(context.parsed.y);
              }
              return label;
            }
          }
        }
      },
      scales: {
        y: {
          grid: {
            color: 'rgba(200, 200, 200, 0.2)',
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };

    // Opções do gráfico de linha estilo financeiro
    const opcoesLinha = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                if (context.dataset.label === 'Variação (%)') {
                  label += context.parsed.y.toFixed(2) + '%';
                } else {
                  label += new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(context.parsed.y);
                }
              }
              return label;
            }
          }
        }
      },
      scales: {
        y: {
          position: 'left',
          title: {
            display: true,
            text: 'Preço (R$)'
          },
          grid: {
            color: 'rgba(200, 200, 200, 0.2)',
            drawBorder: false,
          },
          ticks: {
            callback: function (value) {
              return 'R$ ' + value.toFixed(2);
            }
          }
        },
        y1: {
          position: 'right',
          title: {
            display: true,
            text: 'Variação (%)'
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            callback: function (value) {
              return value.toFixed(2) + '%';
            }
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };

    const opcoesArea = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(context.parsed.y);
              }
              return label;
            },
            afterLabel: function (context) {
              // Calcular a variação percentual entre pontos
              if (context.dataIndex > 0) {
                const currentVal = context.dataset.data[context.dataIndex];
                const prevVal = context.dataset.data[context.dataIndex - 1];
                const variation = ((currentVal - prevVal) / prevVal * 100).toFixed(2);
                const sign = variation >= 0 ? '+' : '';
                return `Variação: ${sign}${variation}%`;
              }
              return null;
            }
          }
        }
      },
      scales: {
        y: {
          grid: {
            color: 'rgba(200, 200, 200, 0.2)',
          },
          ticks: {
            callback: function (value) {
              return 'R$ ' + value.toFixed(2);
            }
          }
        },
        x: {
          grid: {
            display: false
          }
        }
      }
    };

    return (
      <Tab.Container defaultActiveKey="area">
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link eventKey="area">Área</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="financeiro">Financeiro</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="barras">Barras</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pizza">Distribuição</Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="area">
            <div style={{ height: '300px' }}>
              <Line data={dadosArea} options={opcoesArea} />
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="financeiro">
            <div style={{ height: '300px' }}>
              <Line data={dadosLinha} options={opcoesLinha} />
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="barras">
            <div style={{ height: '300px' }}>
              <Bar data={dadosBarras} options={opcoesBarras} />
            </div>
          </Tab.Pane>
          <Tab.Pane eventKey="pizza">
            <div style={{ height: '300px' }}>
              <Pie data={dadosPizza} options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'right',
                  }
                }
              }} />
            </div>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    );
  };

  // Componente para renderizar uma tabela de cotações
  const TabelaCotacoes = ({ dados }) => {
    if (!dados) return <Card.Body className="text-center">Carregando dados...</Card.Body>;

    return (
      <Card className="h-100 shadow-sm">
        <Card.Header className="text-white mb-2"
          style={{
            background: 'linear-gradient(to right, rgba(28, 66, 32, 0.95), rgba(143, 173, 21, 0.9))',
            borderBottom: 'none',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <h5 className="mb-1">{dados.titulo}</h5>
          <small>{dados.fonte}</small>
        </Card.Header>
        <Card.Body className="p-0">
          <Tab.Container defaultActiveKey="tabela">
            <Nav variant="tabs" className="bg-light">
              <Nav.Item>
                <Nav.Link eventKey="tabela">Tabela</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="graficos">Gráficos</Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="tabela">
                <Table hover responsive className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th>Praça</th>
                      <th className="text-center">Preço (R$/sc)</th>
                      <th className="text-center">Variação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.cotacoes.map((cotacao, index) => (
                      <tr key={index}>
                        <td>{cotacao.praca}</td>
                        <td className="text-center fw-bold">{cotacao.preco.toFixed(2)}</td>
                        <td className="text-center">
                          {cotacao.variacao > 0 ? (
                            <Badge bg="success">+{cotacao.variacao.toFixed(2)}%</Badge>
                          ) : cotacao.variacao < 0 ? (
                            <Badge bg="danger">{cotacao.variacao.toFixed(2)}%</Badge>
                          ) : (
                            <Badge bg="secondary">{cotacao.variacao.toFixed(2)}%</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3" className="text-end bg-light">
                        <small className="text-muted">{dados.dataFechamento}</small>
                      </td>
                    </tr>
                  </tfoot>
                </Table>
              </Tab.Pane>
              <Tab.Pane eventKey="graficos" className="p-3">
                <GraficosCotacoes dados={dados} />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Card className="p-3 mb-2">
      {dolar?.ask ? (
        <div className="text-center p-3 mb-3" style={{
          border: '1px solid #dee2e6',
          borderRadius: 5,
          backgroundColor: '#f8f9fa'
        }}>
          <h4 style={{ marginBottom: 0 }}>
            1 Dólar Americano = <span className="text-success fw-bold">{Number(dolar?.ask).toFixed(2)}</span> Real Brasileiro
          </h4>
        </div>
      ) : null}

      <Row className="g-2">
        <Col md={4}>
          <TabelaCotacoes dados={dadosMilho} />
        </Col>
        <Col md={4}>
          <TabelaCotacoes dados={dadosSoja} />
        </Col>
        <Col md={4}>
          <TabelaCotacoes dados={dadosTrigo} />
        </Col>
      </Row>
    </Card>
  );
};

export default Financial;
